import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { useCardData } from '../hooks/UseCardData';
import { useCardView } from '../hooks/UseCardView';
import { CardEditProvider } from './CardEditContext';
import { JobStatus } from '../types/job';
import { DROP_STATUSES } from '../constants/constants';
import { isServerChainJob } from '../hooks/UseServerDaily';

type Data = ReturnType<typeof useCardData>['data'];
type View = ReturnType<typeof useCardView>;

interface CardPageContextValue {
  cards:     Data;
  jobs:      Data['jobs'];
  filters:   View['filters'];
  selection: View['selection'];
  reviewOpen:    boolean;
  setReviewOpen: (v: boolean) => void;
  /** 하락 검토 창을 열 때 먼저 보일 카드(표의 그 줄 '확인' — 감사 card-controltower#457). 없으면 큐 맨 앞. */
  reviewFocusId: number | null;
  /** 그 카드로 하락 검토 창을 연다. */
  openReviewFor: (cardId: number) => void;

  // 잡 결과 모달 (JobModal) — JobProgressBar 는 MainApp 최상위에서 렌더링되므로
  // 페이지 전환과 무관하게 완료 이벤트를 받아 여기서 상태를 갱신한다.
  resultJob:          JobStatus | null;
  resultModalOpen:    boolean;
  setResultModalOpen: (v: boolean) => void;
  onCardJobComplete:  (job: JobStatus) => void;
  /** JobModal의 "하락 확인" → 결과 모달 닫고 DropReviewModal 오픈. CardListPage/DailyWorkflowPage 공용. */
  goToDropReview: () => void;
}

const CardPageContext = createContext<CardPageContextValue | null>(null);

export function CardPageProvider({ children }: { children: ReactNode }) {
  const { data, edit } = useCardData();
  const { filters, selection } = useCardView(data.cards);

  const [reviewOpen, setReviewOpenState] = useState(false);
  const [reviewFocusId, setReviewFocusId] = useState<number | null>(null);
  // 그냥 열고 닫을 때는 보일 카드를 비운다 — 다음에 열 때 지난 카드가 앞에 오지 않게.
  const setReviewOpen = useCallback((v: boolean) => {
    if (!v) setReviewFocusId(null);
    setReviewOpenState(v);
  }, []);
  const openReviewFor = useCallback((cardId: number) => {
    setReviewFocusId(cardId);
    setReviewOpenState(true);
  }, []);

  const [resultJob, setResultJob]           = useState<JobStatus | null>(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);

  /**
   * 카드 잡(수정 가격 조회 등) 완료 콜백.
   * - 비즈니스 로직(카드 머지, updateDone/naverDone 저장)은 jobs.handleJobComplete 에 위임.
   * - 결과 모달 표시 여부 결정(UPDATE_PRICES + 하락 존재 시에만 자동 오픈).
   * - 선택(selection)은 건드리지 않는다(감사 card-controltower#330, 사장 제보 2026-10-29). 예전엔 UPDATE_PRICES 가 끝나면
   *   선택을 하락 + 상승 전체로 채웠는데(옛 카드 목록의 WorkflowStatusBar "총 반영 예정" 용, 2026-07-30), 그 화면은 없어졌고
   *   이 채움이 "매장가 요청" 화면의 체크로 새어 사용자가 고르지 않은 카드가 "네이버 반영" 대상이 됐다. 가격 조회의 ③ 은
   *   선택과 무관하게 전체 카드로 반영하고, "매장가 요청" 은 화면이 따로 가진 선택(사용자가 고른 것만)을 쓴다.
   */
  const onCardJobComplete = useCallback((job: JobStatus) => {
    data.jobs.handleJobComplete(job);
    setResultJob(job);

    if (job.type === 'UPDATE_PRICES' && job.results) {
      const dropIds = job.results
        .filter((c) => DROP_STATUSES.includes(c.priceChangeStatus))
        .map((c) => c.id);

      // 하락이 있으면 곧바로 타 샵 시세 대조 분류로 이어간다(재고 동기화→가격 조회
      // 체인과 같은 방식). 예전엔 여기서 결과 모달(JobModal)을 자동으로 띄워 "하락
      // 확인" CTA를 보여줬지만, 이제는 분류 잡의 상단 진행바 + 완료 토스트 +
      // DropClassificationPanel(3버킷)이 그 자리를 대신한다.
      // 아침 서버 체인의 가격 받기면 분류도 서버가 이어서 한다(감사 #566, 백엔드 PR #78) — 브라우저가 또 시작하지 않는다.
      if (dropIds.length > 0 && !isServerChainJob(job.jobId)) data.jobs.startClassifyDrops(dropIds);
    }
  }, [data.jobs]);

  const goToDropReview = useCallback(() => {
    setResultModalOpen(false);
    setReviewOpen(true);
  }, [setReviewOpen]);

  // 하위 트리(카드 테이블 등)가 무관한 상태 변화만으로 리렌더되지 않도록 value를 메모이즈한다.
  // data/filters/selection은 각자 useCardData/useCardView 안에서 이미 메모이즈돼 있으므로,
  // 여기서도 감싸야 실제로 안 바뀐 렌더에서 참조가 유지된다. edit는 여기 안 넣는다 —
  // CardEditProvider가 별도 컨텍스트로 제공한다(가격 수정 시작/종료마다 이 value가
  // 통째로 바뀌어 PriceCell 전부가 리렌더되는 걸 막기 위함, 2026-08-13).
  const value = useMemo(() => ({
    cards:     data,
    jobs:      data.jobs,
    filters,
    selection,
    reviewOpen, setReviewOpen, reviewFocusId, openReviewFor,
    resultJob, resultModalOpen, setResultModalOpen, onCardJobComplete, goToDropReview,
  }), [
    data, filters, selection,
    reviewOpen, setReviewOpen, reviewFocusId, openReviewFor,
    resultJob, resultModalOpen, setResultModalOpen, onCardJobComplete, goToDropReview,
  ]);

  return (
    <CardEditProvider value={edit}>
      <CardPageContext.Provider value={value}>
        {children}
      </CardPageContext.Provider>
    </CardEditProvider>
  );
}

export function useCardPage(): CardPageContextValue {
  const ctx = useContext(CardPageContext);
  if (!ctx) throw new Error('useCardPage must be used within CardPageProvider');
  return ctx;
}